import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  Input,
  Output,
  EventEmitter,
} from '@angular/core';
import { UntypedFormBuilder, Validators, UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { provideTranslationStatic } from '@skyeng/libs/shared/i18n';
import { TranslocoService } from '@ngneat/transloco';
import { SkyDialogWithAttachmentsAndOptionsApiProvider } from '../../providers/sky-dialog-with-attachments-and-options-api.provider';
import { DsNotificationService } from '@skyeng/libs/shared/ui-kit-v2/notification';
import { ASSETS } from '../../assets';
import { DsBreakpointService } from '@skyeng/libs/shared/ui-kit-v2/layout';
import { concatMap, toArray, switchMap, map } from 'rxjs/operators';
import { UploadingStage } from '../../consts';
import {
  SkyDialogWithAttachmentsAndOptionsSuccessResponse,
  SkyDialogWithAttachmentsAndOptionsSendData,
  SkyDialogWithAttachmentsAndOptionsCloseDialogEvent,
} from '../../interfaces';
import entranslation from './translation/en.json';
import rutranslation from './translation/ru.json';

type FormValue = {
  message: string;
  categories: number[];
};
type FormConfig<ErrorFormValue> = { [key in keyof Required<ErrorFormValue>]: unknown };

interface Progress {
  [key: string]: number;
}

@DecorateUntilDestroy()
@Component({
  selector: 'sky-dialog-with-attachments-and-options',
  templateUrl: 'sky-dialog-with-attachments-and-options.component.html',
  styleUrls: ['sky-dialog-with-attachments-and-options.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [provideTranslationStatic('attachmentsDialog', { en: entranslation, ru: rutranslation })],
  standalone: false,
})
export class SkyDialogWithAttachmentsAndOptionsComponent implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  isLoading$ = new BehaviorSubject(false);
  isError$ = new BehaviorSubject(false);
  activeCategory: number;
  files: File[] = [];
  previewUrls: string[] = [];
  icons = ASSETS;
  messageFocus = false;
  isMobile = this.dsBreakpointService.isSmall();
  activeIndex: number | null = null;
  progress: Progress = {};

  @Input() title: string;
  @Input() categories: string[] = [];
  @Input() maxFiles = 5;
  @Input() isMultiSelect = false;
  @Input() commentIsRequired = false;
  @Input() denyAttach = false;
  @Input() sendCallback: (
    data: SkyDialogWithAttachmentsAndOptionsSendData,
  ) => Observable<SkyDialogWithAttachmentsAndOptionsSuccessResponse | void>;

  @Output() sendAnalytic = new EventEmitter<string>();
  @Output() close = new EventEmitter<SkyDialogWithAttachmentsAndOptionsCloseDialogEvent>();

  constructor(
    private formBuilder: UntypedFormBuilder,
    private apiServiceProvider: SkyDialogWithAttachmentsAndOptionsApiProvider,
    private translateService: TranslocoService,
    private notificationService: DsNotificationService,
    private dsBreakpointService: DsBreakpointService,
    private changeDetectorRef: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.title = this.title?.trim();
    this.initForm();
    this.subscribeOnUploading();
  }

  onClose(rejected = true): void {
    this.close.emit({ rejected });
  }

  submit(): void {
    if (!this.form.valid) {
      return;
    }
    this.previewUrls.forEach(url => (this.progress = { ...this.progress, [url]: 0 }));

    this.isLoading$.next(true);
    this.isError$.next(false);

    const formValue = this.form.value as FormValue;
    const { message, categories } = formValue;

    from(this.files)
      .pipe(
        concatMap((file, index) => {
          return this.apiServiceProvider.uploadFile(file, this.previewUrls[index]);
        }),
        toArray(),
        map((data: ({ id: number; file: string } | null)[]) => {
          return {
            resourcesIds: data.map(value => value?.id).filter(id => id !== null) as number[],
            resourcesUrls: data.map(value => value?.file).filter(id => id !== null) as string[],
          };
        }),
        switchMap((data: { resourcesIds: number[]; resourcesUrls: string[] }) => {
          return this.sendCallback({
            message,
            resourceIds: data.resourcesIds,
            categories,
            resourceUrls: data.resourcesUrls,
          });
        }),
        takeUntilDestroyed(this),
      )
      .subscribe({
        next: () => this.onSubmitSuccess(),
        error: () => this.onSubmitError(),
      });
  }

  onFilesDropped(files: FileList | File[]): void {
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      const [type] = file.type.split('/');
      if (type !== 'image') {
        continue;
      }
      if (this.files.length >= this.maxFiles) {
        this.notificationService.openWarningNotification({
          content: this.translateService.translate('attachmentsDialog.error.maxFiles', { count: this.maxFiles }),
        });
        return;
      }
      this.files = [...this.files, file];
      this.updatePreviewUrls();
    }
  }

  setCategories(index: number | string, isChecked?: boolean): void {
    if (this.isMultiSelect) {
      this.setMultipleCategories(isChecked ?? false, index);
      return;
    }
    this.activeCategory = +index;
    this.form.setValue({ ...this.form.value, categories: [this.activeCategory + 1] });
  }

  removeFile(index: number): void {
    const url = this.previewUrls[index];
    URL.revokeObjectURL(url);

    this.previewUrls = this.previewUrls.filter((_, i) => i !== index);
    this.files = this.files.filter((_, i) => i !== index);
  }

  ngOnDestroy(): void {
    this.previewUrls.forEach(url => URL.revokeObjectURL(url));
  }

  setMessageFocus(): void {
    this.messageFocus = true;
  }

  setMessageBlur(): void {
    if (!this.form.get('message')?.valid) {
      this.messageFocus = false;
    }
  }

  openImage(index: number): void {
    this.activeIndex = index;
  }

  closeImage(): void {
    this.activeIndex = null;
  }

  private initForm(): void {
    const formConfig: FormConfig<FormValue> = {
      message: [null],
      categories: [null, [Validators.required, Validators.minLength(1)]],
    };
    this.form = this.formBuilder.group(formConfig);
    if (this.commentIsRequired) {
      this.form.get('message')?.addValidators([Validators.required, Validators.minLength(5)]);
    }
  }

  private onSubmitSuccess(): void {
    this.isLoading$.next(false);
    this.notificationService.openSuccessNotification({
      content: this.translateService.translate('attachmentsDialog.success.send'),
    });
    this.sendAnalyticEvent();

    this.close.emit({
      rejected: false,
      value: this.form.get('message')?.value,
      categories: this.form.get('categories')?.value,
    });
  }

  private onSubmitError(): void {
    this.isLoading$.next(false);
    this.notificationService.openWarningNotification({
      content: this.translateService.translate('attachmentsDialog.error.send'),
    });
    this.progress = {};
    this.isError$.next(true);
  }

  private subscribeOnUploading(): void {
    this.apiServiceProvider.onUploading(uploadingEvent => {
      if (!uploadingEvent.initiator || !this.previewUrls.includes(uploadingEvent.initiator)) {
        return;
      }
      if (![UploadingStage.PROGRESS, UploadingStage.PROCESSING].includes(uploadingEvent.stage)) {
        return;
      }
      const event = uploadingEvent.event as ProgressEvent;
      const progress = event.total > 0 ? Math.round((event.loaded / event.total) * 100) : 0;
      this.progress = { ...this.progress, [uploadingEvent.initiator]: progress };

      this.changeDetectorRef.markForCheck();
    });
  }

  private sendAnalyticEvent(): void {
    const comment = this.form.get('message')?.value;
    this.sendAnalytic.emit(comment);
  }

  private setMultipleCategories(isChecked: boolean, index: number | string): void {
    const currentCategories = (this.form.get('categories')?.value ?? []) as number[];
    if (isChecked) {
      this.form.setValue({ ...this.form.value, categories: [...currentCategories, index] });
    } else {
      this.form.setValue({ ...this.form.value, categories: currentCategories.filter(item => item !== index) });
    }
  }

  private updatePreviewUrls(): void {
    this.previewUrls.forEach(url => URL.revokeObjectURL(url as string));
    this.previewUrls = [];
    this.files.forEach(file => {
      const url = URL.createObjectURL(file);
      this.previewUrls = [...this.previewUrls, url];
    });
  }
}
